import { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Heading from '@tiptap/extension-heading';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import HardBreak from '@tiptap/extension-hard-break';
import Horizontalrule from '@tiptap/extension-horizontal-rule';
import Blockquote from '@tiptap/extension-blockquote';
import BulletList from '@tiptap/extension-bullet-list';
import ListItem from '@tiptap/extension-list-item';
import Typography from '@tiptap/extension-typography';
import OrderedList from '@tiptap/extension-ordered-list';
import Code from '@tiptap/extension-code';
import CodeBlock from '@tiptap/extension-code-block';
import Image from '@tiptap/extension-image';
import FileHandler from '@tiptap-pro/extension-file-handler';
import Dropcursor from '@tiptap/extension-dropcursor';
import History from '@tiptap/extension-history';
import Placeholder from '@tiptap/extension-placeholder';
import Gapcursor from '@tiptap/extension-gapcursor';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import { useEditor, EditorContent } from '@tiptap/react';
import MarkdownIt from 'markdown-it';

import { useAuth } from '@/auth/auth-context';
import { TextCell } from '@/explore/types';
import { Icon } from '@/components/icon';
import { useLayoutContext } from '@/components/layout/layout-context';
import { useAccountContext } from '@/lib/accounts/context';
import { useTrackEvent } from '@/lib/analytics';
import { notNil } from '@/lib/utils';
import { getExplorationLayout } from '@/core/exploration';
import { shouldPropagateEvent } from '@/explore/utils/interactable';

import { useExplorationCellContext } from '../exploration-cell-context';
import { useExplorationContext } from '../exploration-context';
import { CellControls } from '../cell-controls';
import UploadingImage from './uploading-image';
import { PopupMenu } from './popup-menu';
import { ConfluenceReferenceNode } from './extensions/nodes/confluence-reference-node';
import { SlackReferenceNode } from './extensions/nodes/slack-reference-node';
import { GoogleDriveReferenceNode } from './extensions/nodes/google-drive-reference-node';
import { NotionReferenceNode } from './extensions/nodes/notion-reference-node';
import { HiddenAINode } from './extensions/nodes/hidden-ai-node';
import { FootnoteListNode } from './extensions/nodes/footnote-list-node';
import { createFootnoteNode } from './extensions/nodes/footnote-node';
import { Video } from './extensions/nodes/video-node';
import { CustomKeyboardShortcuts } from './extensions/shortcuts';
import { EnhancedLink } from './extensions/link';
import {
  ensureReferenceBlocks,
  ensureFootnoteFormatting,
  ensureHiddenAINodeFormatting,
  escapeAttributes,
  htmlCustomTags,
} from './utils/markdown';

import styles from './text-cell.module.scss';
import explorationStyles from '../exploration.module.scss';

interface TextCellViewProps {
  cell: TextCell;
  selected: boolean;
  onSelectCell?: () => void;
  onSetDraggable: (value: boolean) => void;
}

const extensions = [
  Document,
  Paragraph,
  Text,
  Image,
  Heading.configure({ levels: [1, 2, 3] }),
  Bold,
  Italic,
  Underline,
  HardBreak,
  EnhancedLink.configure({
    openOnClick: false,
    protocols: ['http', 'https'],
    defaultProtocol: 'https',
  }),
  Horizontalrule,
  Blockquote,
  BulletList,
  OrderedList,
  ListItem,
  Typography,
  Code,
  CodeBlock,
  Video,
  FileHandler.configure({
    allowedMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic'],
    onDrop: (editor, files, pos) => files.forEach((file) => editor.commands.uploadFile(pos, file)),
    onPaste: (editor, files, htmlContent) => {
      files.forEach((file) => {
        if (htmlContent !== undefined && htmlContent !== null && htmlContent.length > 0) {
          // if there is htmlContent, stop manual insertion & let other extensions handle insertion via inputRule
          // you could extract the pasted file from this url string and upload it to a server for example
          return false;
        }

        editor.commands.uploadFile(editor.state.selection.anchor, file);
      });
    },
  }),
  Dropcursor,
  Placeholder.configure({ placeholder: 'Text...' }),
  Gapcursor,
  History,
  FootnoteListNode,
  ConfluenceReferenceNode,
  SlackReferenceNode,
  GoogleDriveReferenceNode,
  NotionReferenceNode,
  HiddenAINode,
  CustomKeyboardShortcuts,
  Table.configure({ resizable: false }),
  TableRow,
  TableHeader,
  TableCell,
];

const md = MarkdownIt({
  breaks: true,
  html: true,
}).use(htmlCustomTags);

const convertContent = (cell: TextCell) => {
  const title = cell.title ?? '';
  const content = `${(title.length ?? 0) > 0 ? `<h1>${cell.title}</h1>` : ''}${cell.content ?? ''}`;
  return md.render(
    escapeAttributes(
      ensureHiddenAINodeFormatting(ensureFootnoteFormatting(ensureReferenceBlocks(content))),
    ),
  );
};

export const TextCellView = (props: TextCellViewProps) => {
  const { cell } = props;

  const { getToken } = useAuth();
  const { account } = useAccountContext();
  const { exploration } = useExplorationContext();
  const { setCell, isSelectedCell, isConversationPart } = useExplorationCellContext();
  const { isEmbeddedMode } = useLayoutContext();
  const trackEvent = useTrackEvent();
  const containerRef = useRef<HTMLDivElement>(null);

  const editor = useEditor({
    extensions: extensions.concat([
      UploadingImage.configure({
        accountId: account.accountId,
        getToken,
        onUploadSuccess: (fileId) =>
          trackEvent('Image Uploaded', {
            fileId,
            explorationId: exploration.explorationId,
            cellId: cell.id,
          }),
        onUploadError: (fileId) =>
          trackEvent('Image Upload Failed', {
            fileId,
            explorationId: exploration.explorationId,
            cellId: cell.id,
          }),
      }),
      createFootnoteNode(cell.id),
    ]),
    content: convertContent(cell),
    editable: !isEmbeddedMode,
  });

  useEffect(() => {
    editor?.commands.setTextSelection(0);
  }, [editor]);

  useEffect(() => {
    const editorContent = editor?.getHTML();

    if (editor !== null && cell.content !== editorContent) {
      editor.commands.setContent(convertContent(cell));
    }
  }, [cell, editor]);

  useEffect(() => {
    const hasFocus = editor?.view.hasFocus() ?? false;
    if (isSelectedCell && !hasFocus) {
      editor?.view.focus();
    }
    if (!isSelectedCell && hasFocus) {
      editor?.commands.blur();
    }
  }, [isSelectedCell, editor]);

  /**
   * Disable zooming the page if we're pinching in canvas; temporary workaround for this `nowheel` bug
   * https://github.com/xyflow/xyflow/issues/5074#issuecomment-2773442591
   */
  const handleScroll = useCallback(
    (event: WheelEvent) => {
      if (event.ctrlKey && getExplorationLayout(exploration) === 'canvas') {
        event.preventDefault();
      }
    },
    [exploration],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleScroll, { passive: false });
    }
    return () => {
      if (container) {
        container.removeEventListener('wheel', handleScroll);
      }
    };
  }, [containerRef, handleScroll]);

  const handleEditorBlur = () => {
    if (editor === null) {
      return;
    }

    const content = editor.getHTML();

    if (cell.content !== content) {
      setCell({ ...cell, content, title: undefined });
    }
  };

  return (
    <div ref={containerRef} className={styles.textCell}>
      {!isConversationPart && (
        <Icon
          name="DragHandle"
          size={10}
          className={classNames(explorationStyles.dragHandle, styles.dragHandle)}
          onMouseOver={() => props.onSetDraggable(true)}
          onMouseOut={() => props.onSetDraggable(false)}
        />
      )}

      {/*
        div here is necessary for PopupMenu keyboard navigation support
        Read more: https://atomiks.github.io/tippyjs/v6/accessibility/#interactivity
      */}
      <div>
        <EditorContent
          className="nowheel" // Enable scrolling content in canvas
          editor={editor}
          onBlur={handleEditorBlur}
          onClick={(e) => {
            const linkElement = (e.target as HTMLElement).closest('a');

            if (!linkElement && shouldPropagateEvent(e)) {
              props.onSelectCell?.();
              return;
            }

            const href = linkElement?.getAttribute('href');

            if (!isSelectedCell && notNil(href)) {
              window.open(href, '_blank', 'noopener noreferrer');
              return;
            }

            if (!e.metaKey && !e.ctrlKey) {
              editor?.chain().extendMarkRange('link').run();
            }
          }}
        />
        <PopupMenu editor={editor} />
      </div>

      <div className={styles.controls}>
        <CellControls exploration={exploration} />
      </div>
    </div>
  );
};
